import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MediaKind } from './media-api';
import { MediaIconComponent } from './media-icon.component';
import { MediaPicDirective } from './media-preview-cache';
import { TileThumb } from './media-tiles';

interface Pic {
  kind: 'public' | 'private' | 'local' | 'none';
  src: string | null;
  srcset: string | null;
  url: string | null;
  layer: { width: string; left: string; top: string } | null;
}

/**
 * The picture of an item, filling the box it is put in: the public WebP of a published item (1x / 2x), the private preview of the job (cut to the frame when the frame
 * is known), the file the editor just chose, or an empty field with the sign of the kind.
 */
@Component({
  selector: 'hc-media-thumb',
  imports: [MediaIconComponent, MediaPicDirective],
  template: `
    @if (pic().kind === 'public') {
      <img class="fill" [src]="pic().src" [attr.srcset]="pic().srcset" alt="" draggable="false" />
    } @else if (pic().kind === 'private') {
      <img [class.fill]="!pic().layer" [class.layer]="!!pic().layer" [hcMediaPic]="pic().url" [style.width]="pic().layer?.width" [style.left]="pic().layer?.left" [style.top]="pic().layer?.top" alt="" draggable="false" />
    } @else if (pic().kind === 'local') {
      <img class="fill" [src]="pic().src" alt="" draggable="false" />
    } @else {
      <span class="empty"><hc-media-icon [name]="kind() === 'VIDEO' ? 'video' : 'image'" [size]="iconSize()" [width]="1.5"></hc-media-icon></span>
    }
  `,
  styles: [
    `
      :host { position: absolute; inset: 0; display: block; overflow: hidden; }
      img { display: block; user-select: none; -webkit-user-drag: none; }
      img.fill { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
      img.layer { position: absolute; height: auto; max-width: none; }
      .empty { position: absolute; inset: 0; display: grid; place-items: center; color: #bfbfbf; }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaThumbComponent {
  readonly thumb = input.required<TileThumb>();
  readonly kind = input<MediaKind>('PHOTO');
  readonly iconSize = input(28);

  protected readonly pic = computed<Pic>(() => {
    const thumb = this.thumb();
    switch (thumb.type) {
      case 'public':
        return { kind: 'public', src: thumb.src, srcset: thumb.srcset, url: null, layer: null };
      case 'private':
        return { kind: 'private', src: null, srcset: null, url: thumb.url, layer: thumb.layer };
      case 'local':
        return { kind: 'local', src: thumb.url, srcset: null, url: null, layer: null };
      default:
        return { kind: 'none', src: null, srcset: null, url: null, layer: null };
    }
  });
}
